/**
 * Sanitizes a pathname into an aggregatable route name suitable for sending
 * to New Relic Browser via `window.newrelic.setCurrentRouteName(...)`.
 *
 * Why
 * ---
 * NR's SPA agent auto-detects route changes via History API monkey-patching
 * and uses the raw URL by default. For Expo Router we have dynamic segments
 * like `/app/league-detail/<leagueId>` or `/[slug]` that, left raw, would:
 *   1. Leak PII / opaque IDs into a third-party vendor.
 *   2. Explode cardinality so error grouping by route becomes useless.
 *
 * Strategy
 * --------
 * Replace each dynamic-looking path segment with `:id`:
 *   - UUID v1-v8 (8-4-4-4-12 hex)
 *   - Pure numeric segments
 *   - Long opaque tokens (>= 16 chars, mostly base-58 / base-64-ish)
 * Everything else passes through unchanged so well-known routes keep their
 * literal names (`/app/index`, `/sign-in`, `/account-admin`).
 *
 * Pure function. Exported for unit testing.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;
const OPAQUE_TOKEN_RE = /^[A-Za-z0-9_-]{16,}$/;

export function sanitizeRouteForAnalytics(pathname: string | null | undefined): string {
  if (!pathname) return '/';
  const queryStripped = pathname.split('?')[0].split('#')[0];
  if (queryStripped === '/' || queryStripped === '') return '/';

  const parts = queryStripped.split('/');
  const sanitized = parts.map((segment) => {
    if (segment === '') return segment;
    if (UUID_RE.test(segment)) return ':id';
    if (NUMERIC_RE.test(segment)) return ':id';
    if (OPAQUE_TOKEN_RE.test(segment)) return ':id';
    return segment;
  });
  return sanitized.join('/');
}
